/**
 * Binding edits, from Learn and the MIDI panel. Each goes through `learnEdit`, so one bind, unbind or setting change is one step of Learn's own undo (`input.undo`), which covers the controller bindings and the key bindings keyboard learn writes.
 */
import { enableMidi } from '~/lib/input'
import { sameSource } from '~/lib/input/model'
import { setKeyBindings, updateBinding as updateKeyBinding } from '~/store/keyBindings'
import { registerAction } from '~/store/middleware/confirmActions'
import { requestConfirm } from '~/store/ui'
import {
	learnBinding,
	pushLearnUndo,
	removeBinding,
	removeTargetBindings,
	setBindings,
	setLearn,
	setLearnTaken,
	stepLearnUndo,
	updateBinding,
} from '.'

const snapshot = s => ({ bindings: s.input.bindings, keys: s.key_bindings.bindings })

/** Run `fn(dispatch, getState)`; record one undo step when it changed a binding. */
export const learnEdit = fn => (dispatch, getState) => {
	const before = snapshot(getState())
	fn(dispatch, getState)
	const after = snapshot(getState())
	if (after.bindings !== before.bindings || after.keys !== before.keys) dispatch(pushLearnUndo(before))
}

/** Undo (or redo) one Learn edit; false when there is none. */
export const stepLearn = redo => (dispatch, getState) => {
	const s = getState()
	const { past, future } = s.input.undo
	const snap = redo ? future[0] : past[past.length - 1]
	if (!snap) return false
	dispatch(stepLearnUndo({ redo, current: snapshot(s) }))
	dispatch(setBindings(snap.bindings))
	dispatch(setKeyBindings(snap.keys))
	return true
}

/**
 * Enter or leave Learn; `on` omitted toggles. ⚠️ Entering is the user action MIDI access waits for (Chrome prompts only for one), so it asks here. Leaving keeps MIDI running: the bindings are for the show, not for Learn.
 */
export const toggleLearn = on => (dispatch, getState) => {
	const next = on ?? !getState().input.learn.on
	dispatch(setLearn(next))
	if (next) enableMidi()
}

const noteTaken = (target, from) => setLearnTaken(from.length ? { target, from } : null)

/** Bind what learn heard (`{ source, mode, encoding }`) to `target`, a target string or entry. Without `add` the control leaves the targets it drove, which `learn.taken` names. */
export const bindLearned = (learned, target, add) => (dispatch, getState) => {
	const t = typeof target === 'string' ? target : target.target
	const from = add
		? []
		: getState()
				.input.bindings.filter(b => sameSource(b, learned))
				.flatMap(b => b.targets.map(x => x.target))
				.filter(x => x !== t)
	dispatch(learnEdit(d => d(learnBinding({ binding: { ...learned, targets: [target] }, add }))))
	dispatch(noteTaken(t, [...new Set(from)]))
}

export const unbindTarget = target => learnEdit(dispatch => dispatch(removeTargetBindings(target)))

/** One binding stops writing `target`; a binding left with no target goes. */
export const unbindBinding = (id, target) =>
	learnEdit((dispatch, getState) => {
		const b = getState().input.bindings.find(x => x.id === id)
		if (!b) return
		const targets = b.targets.filter(t => t.target !== target)
		dispatch(targets.length ? updateBinding({ id, patch: { targets } }) : removeBinding(id))
	})

export const editBinding = (id, patch) => learnEdit(dispatch => dispatch(updateBinding({ id, patch })))

/** Every MIDI mapping goes, as one undo step. */
const clearMidiBindings = () =>
	learnEdit((dispatch, getState) =>
		dispatch(setBindings(getState().input.bindings.filter(b => b.source?.backend !== 'midi')))
	)

registerAction('clearMidiBindings', clearMidiBindings)

export const confirmClearMidiBindings = () =>
	requestConfirm({
		title: 'Clear MIDI Mappings',
		message: 'Remove every MIDI mapping? Undo brings them back.',
		action: { type: 'clearMidiBindings' },
		yesLabel: 'Clear',
		destructive: true,
	})

/** Keyboard learn: `hotkey` null unbinds. Moves the key off any other action, as Settings does; `learn.taken` names them. */
export const bindKey = (action, hotkey) => (dispatch, getState) => {
	const had = getState().key_bindings.bindings
	dispatch(learnEdit(d => d(updateKeyBinding({ action, hotkey: hotkey || undefined }))))
	const now = getState().key_bindings.bindings
	const from = hotkey ? Object.keys(had).filter(a => a !== action && had[a] === hotkey && now[a] !== hotkey) : []
	dispatch(
		noteTaken(
			`action:${action}`,
			from.map(a => `action:${a}`)
		)
	)
}
